import axios from "axios";
import publicConfig from "config/public";
import cookie from "cookie";
import { IncomingMessage, ServerResponse } from "http";
import pick from "lodash/pick";

import { Location } from "custom-types/Location";
import logError from "utils/logError";

import { GeolocationResult } from "./getGeolocation";

const FALLBACK_LOCATION: Location = {
  city: "Seattle",
  coordinates: { latitude: 47.6062095, longitude: -122.3320708 },
  country: "United States",
  countryCode: "US",
  country_code: "US",
  defaultLocation: true,
  formattedLocation: "Seattle, WA",
  formatted_location: "Seattle, WA",
  geoSlug: "seattle-wa-us",
  place_id: "ChIJVTPokywQkFQRmtVEaUZlJRA",
  slug: "seattle-wa-us",
  state: "Washington",
  state_code: "WA",
  street: { name: "", number: "" },
  sublocality: "",
  zip: "98164",
};

const { cookieDomain, cookieDomainCa, leaflyDomain, leaflyCaDomain, services } =
  publicConfig;

const geoIpApiUrl = services?.geoIpApi?.url;
export const getUserLocationData = async (
  domainCountryCode: string,
  ctx?: { req?: IncomingMessage; res?: ServerResponse },
  fetchClientSide = false
): Promise<Location> => {
  const req = ctx?.req;
  const res = ctx?.res;

  try {
    let location: Location | undefined;
    let usingCookieLocation = false;

    if (!fetchClientSide && !(req || res)) {
      location = FALLBACK_LOCATION;
    } else {
      try {
        if (req) {
          location = getUserLocationByCookie(req);
          usingCookieLocation = !!location;
        }

        if (!location) {
          location = await getUserLocationByIp(
            req,
            domainCountryCode,
            fetchClientSide
          );

          if (location) {
            location.isUserLocation = true;
          }
        }

        if (!location) {
          location = FALLBACK_LOCATION;
        }
      } catch (error) {
        location = FALLBACK_LOCATION;
      }

      if (!location) {
        throw new Error("failed to get user location");
      }

      if (res && !usingCookieLocation) {
        setLocationCookie(location, res, domainCountryCode);
      }
    }

    return {
      ...location,
      countryCode: location.country_code,
      formattedLocation: location.formatted_location,
      geoSlug: location.slug,
    };
  } catch (e) {
    logError(e.message || "Error processing data", {
      functionName: "getUserLocationData",
    });

    return FALLBACK_LOCATION;
  }
};

const getUserLocationByCookie = (
  req: IncomingMessage
): Location | undefined => {
  if (!req.headers.cookie) {
    return;
  }

  const parsedCookie = cookie.parse(req.headers.cookie);
  const locationCookie = parsedCookie["leafly-location"];

  if (locationCookie) {
    return JSON.parse(locationCookie);
  }

  return;
};

const getUserLocationByIp = async (
  req?: IncomingMessage,
  domainCountryCode = "US",
  fetchClientSide = false
): Promise<Location | undefined> => {
  try {
    let ip;
    if (req) {
      ip = getUserIp(req);
    }

    if (!geoIpApiUrl || (!fetchClientSide && !ip)) {
      return;
    }

    if (ip && ip.slice(0, 7) === "::ffff:") {
      ip = ip.slice(7);
    }

    let requestUrl = `${geoIpApiUrl}/geo/v1/geoip/${ip}`;
    if (fetchClientSide && !ip) {
      const domain = domainCountryCode === "CA" ? leaflyCaDomain : leaflyDomain;
      requestUrl = `${domain}/geo/v1/geoip`;
    }

    const response = await axios.get<GeolocationResult>(requestUrl);
    if (response && response.status === 200) {
      return pick(response.data, [
        "city",
        "coordinates",
        "country_code",
        "country",
        "formatted_location",
        "place_id",
        "slug",
        "state_code",
        "state",
        "street",
        "sublocality",
        "zip",
      ]);
    }
  } catch (err) {
    return;
  }
};

const getUserIp = (req: IncomingMessage): string | undefined => {
  const xForwardedFor = req.headers["x-forwarded-for"];

  if (xForwardedFor) {
    return String(xForwardedFor).split(",").pop();
  }

  return req.socket.remoteAddress;
};

const setLocationCookie = (
  location: Location,
  res: ServerResponse,
  domainCountryCode: string
): void => {
  const domain = domainCountryCode === "CA" ? cookieDomainCa : cookieDomain;

  res.setHeader(
    "Set-Cookie",
    cookie.serialize("leafly-location", JSON.stringify(location), {
      domain,
      maxAge: 86400 * 365 * 1000,
      path: "/",
    })
  );
};
